import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';

import { Body as DefaultBody } from './Details';

import ThemeContext from '../../utils/theming';
import { width, widthEm, isPortrait } from '../../utils/size';
import { getImageUrl } from '../../utils/imageLoading';
import multiLineElipsis from '../../components/Shared/mixins';
import useFitText from "../../utils/useFitText";

export const ScrollNotice = styled.div`
    display: flex;
    align-items: center;
    height: 30px;
    border-radius: 14.5px;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.3);
    background-color: white;
    transform: scale(1);
    transition: transform 0.25s ease;

    span {
        font-family: Lato;
        font-size: 12px;
        font-weight: bold;
        line-height: 1;
        color: ${({ theme }) => theme.colors.primary};
        padding: 0 1em;
        white-space: nowrap;
    }

    &:hover,
    &:active,
    &:focus {
        transform: scale(1.05);
    }
`;

export const SpinnerContainer = styled.div`
    width: 100%;
    display: flex;
    flex: 1;
    margin: 0 auto;
    justify-content: center;
    align-content: center;
    position: relative;
`;

const calculateTitleSize = (
    isPortrait,
    isShortText,
    isLongText,
    isExtraLongText,
    scaleFactor = 1
) => {
    const defaultSize = isShortText ? 32 : isExtraLongText ? 24 : isLongText ? 28 : 30;
    const widthRatio = Math.min(1, width() / (isPortrait ? 375 : 620));
    return defaultSize * scaleFactor * widthRatio;
};
const calculateDescriptionSize = (
    isPortrait,
    isLongText,
    isExtraLongText,
    scaleFactor = 1
) => {
    const defaultSize = isExtraLongText ? 16 : isLongText ? 18 : 20;
    const widthRatio = Math.min(1, width() / (isPortrait ? 375 : 620));
    return defaultSize * scaleFactor * widthRatio;
};

export const FooterSpan = styled.div`
    font-size: 10px;
    color: #000;
    font-weight: bold;
    line-height: 8px;

    a,
    span {
        font-weight: normal;
        padding: 0;
        font-family: Lato;
        line-height: 1.6;
        color: #008eff;
        text-decoration: none;
        white-space: nowrap;
        cursor: pointer;
    }
`;

const TitleComponent = styled.h2`
    font-family: Averta;
    font-weight: 300;
    margin: 0;
    word-break: break-word;
    &::first-letter {
        text-transform: uppercase;
    }

    ${props => !props.isAutoScale && `
        font-size: ${
            props.isVideoCard
                ? '22px'
                : calculateTitleSize(
                      isPortrait(),
                      props.isShortText,
                      props.isLongText,
                      props.isExtraLongText,
                      props.scaleFactor
                  ) + 'px'};
        line-height: ${
            props.isVideoCard
                ? '28px'
                : calculateTitleSize(
                      isPortrait(),
                      props.isShortText,
                      props.isLongText,
                      props.isExtraLongText,
                      props.scaleFactor
                  ) +
                  5 +
                  'px'};
    `}

    span {
        font-weight: 800;
        color: ${({ theme }) => theme.colors.primary};
        cursor: pointer;
    }

    ${props =>
        props.isLocalRepLoading &&
        `background-image: linear-gradient(to left, #f8f8f7, #f3f3f0 50%, #f8f8f7);
        text-indent: -9999px;
        margin-bottom: 12px;`};
    ${props => props.useMultiLineElipsis && multiLineElipsis(1, isPortrait() ? 3 : 5)}
`;

export const Title = props =>
    <TitleComponent theme={useContext(ThemeContext)} {...props}>
            {props.children}
    </TitleComponent>

Title.propTypes = {
    children: PropTypes.object,
    useMultiLineElipsis: PropTypes.bool
};

export const AutoScalingTitle = props => {
    const { fontSize, ref } = useFitText({maxFontSize:150});
    return (<TitleComponent
        isAutoScale
        ref={ref}
        theme={useContext(ThemeContext)}
        {...props}
        style={{ ...props.style, fontSize }}
    />);
}

export const DescriptionComponent = styled.p`
    object-fit: contain;
    font-family: Lato;
    line-height: 1.5;
    color: #333;
    margin-bottom: 0;
    word-break: break-word;
    font-size: 0.915em;

    &::first-letter {
        text-transform: uppercase;
    }
    ${props => props.bold && `font-weight: 700;`};

    ${props => !props.isAutoScale && `
        font-size: ${
            calculateDescriptionSize(
                isPortrait(),
                props.isLongText,
                props.isExtraLongText,
                props.scaleFactor
            ) + 'px'};
        line-height: ${
            calculateDescriptionSize(
                isPortrait(),
                props.isLongText,
                props.isExtraLongText,
                props.scaleFactor
            ) +
            5 +
            'px'};
    `}

    margin-top: ${props => (props.isTargetedMultiAction && isPortrait() ? `10px` : 0)};
    ${props => props.isLocalRepLoading && `text-indent: -9999px;`};

    span {
        font-weight: 800;
        color: ${({ theme }) => theme.colors.primary};
        cursor: pointer;
    }

    b {
        font-weight: 900;
    }

    ${props => props.isLocalRepLoading && `
        &:before {
            content: "";
            background-image: linear-gradient(to left, #f8f8f7, #f3f3f0 50%, #f8f8f7);
            width: 100%;
            height: 20px;
            margin-bottom: 8px;
            float: left;
        }

        &:after {
            content: "";
            background-image: linear-gradient(to left, #f8f8f7, #f3f3f0 50%, #f8f8f7);
            width: 80%;
            height: 20px;
            float: left;
        }
    `}
`;

export const Description = props =>
    <DescriptionComponent theme={useContext(ThemeContext)} {...props}>
        {props.children}
    </DescriptionComponent>

Description.propTypes = {
    children: PropTypes.object
};

export const AutoScalingDescription = props => {
    const { fontSize, ref } = useFitText();
    return (
        <DescriptionComponent
            isAutoScale
            ref={ref}
            theme={useContext(ThemeContext)}
            {...props}
            style={{ ...props.style, fontSize }}
        />);
};

export const ActionContainer = styled.div`
    display: flex;
    flex: 1;
    justify-content: flex-end;
    flex-direction: ${props =>
        props.isSingleAction && widthEm() < 30 ? `column` : `row`};
    margin-bottom: 40px;
    width: 100%;

    ${props =>
        widthEm() <= 30 &&
        `margin-left: auto;
        flex-direction: column;
        ${props.isLocalRepCard}width: calc(100% - 60px - 1em)`};

    button {
        ${props =>
            props.isLocalRepLoading &&
            `text-indent: -9999px;
            background-image: linear-gradient(to left, #f8f8f7, #f3f3f0 50%, #f8f8f7);
            border: none !important;`};
    }
`;

export const Body = styled(DefaultBody)`
    ${props =>
        props.isBlured &&
        `filter: blur(1px);
        opacity: 0.8;
        background-color: rgba(255, 255, 255, 0.8);`};
`;

export const PeopleSigned = styled.p`
    &::first-letter {
        text-transform: uppercase;
    }
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 2px;
    color: #c9c3c3;
    font-weight: bold;
    margin-top: 10px;
`;

export const Content = styled.div`
    display: flex;
    ${props =>
        (props.isVideoCard || props.isSentimentPoll || props.flexGrow) && `flex: 1`};
    flex-direction: column;
    margin: 10px 0 10px 0;
    ${props => props.hasNextAction && `margin-bottom: 50px;`};
    ${props => props.isVisualAction && isPortrait() && `margin-bottom: 0;`};
    font-size: 18px;
    ${props =>
        widthEm() <= 30 &&
        ` margin-top: 1em;
            ${props.isContactAction &&
                !props.useLocalRepresentative &&
                `flex-direction: column;`}
    `};
`;

export const DetailsContainer = styled.div`
    margin-top: ${props => (props.singleImage ? 20 : 8)}px;
    display: flex;
    flex: 1;
    flex-direction: row;
    ${props => (props.isVideoCard ? `justify-content: center` : `align-items: center`)};
    ${() => isPortrait() && `flex-direction: column;`};
    ${props => props.flexStart && `align-items: flex-start;`};
`;

export const Details = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    height: ${props => !props.actionChoice && 100}%;
    ${props => props.hasPhoto && !isPortrait() && `margin-left: 1em;`};
    ${props => props.isRepStance && isPortrait() && `margin-top: 0.5em;`};
    ${props => props.isRepStance && `justify-content: center;`};
    ${() => isPortrait() && `align-self: flex-start;`};
`;

export const Footer = styled.footer`
    display: flex;
    width: 100%;
    height: 40px;
    justify-content: space-between;
    align-self: flex-end;
`;

export const RTVLogo = getImageUrl('rock-the-vote-logo.png', 'DEFAULT_IMAGES_URL');
export const DefaultRepImageURL = country => {
    switch ((country || '').toLowerCase()) {
        case 'uk':
        case 'gb':
            return getImageUrl('ukParliamentDefault.png', 'DEFAULT_IMAGES_URL');
        default:
            return getImageUrl('localRepDefault.jpg', 'DEFAULT_IMAGES_URL');
    }
};

const CountryFlagContainer = styled.img`
    width: 100px;
    margin-top: -6px;
`

export const CountryFlag = props =>
    <CountryFlagContainer src={`https://www.countryflags.io/${props.countryCode}/flat/64.png`} />
